import { useEffect, useState, type KeyboardEvent } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Rocket, X, Sparkles } from 'lucide-react';
import type { IpSeed } from '@starfox/shared-types';
import type { AudienceState } from './WriterPage';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ipSeed: IpSeed | null;
  audience: AudienceState;
  episodeCount: number;
  storylineQuick: string;
  fullStorylineText: string | null;
  plotsCount?: number;
  initialGenreTags?: string[];
  onConfirm: (payload: { title: string; genreTags: string[] }) => void;
  submitting: boolean;
}

const GENRE_PRESETS = ['都市情感', '甜宠', '逆袭', '悬疑', '玄幻', '校园', '职场', '霸总', '复仇', '穿越'];

function HongguoHint({ genreTags }: { genreTags: string[] }) {
  if (genreTags.length === 0) return null;
  return (
    <p className="mt-1.5 rounded-lg bg-brand-50 px-2.5 py-1.5 text-[11px] text-brand-700">
      默认按普通（红果）生成：强钩子、快节奏、台词驱动，并在后续 S 场景校验首尾钩子、节奏点和媒介 OS。
    </p>
  );
}

export function InitializeConfirmDialog({
  open,
  onOpenChange,
  ipSeed,
  audience,
  episodeCount,
  storylineQuick,
  fullStorylineText,
  plotsCount = 0,
  initialGenreTags,
  onConfirm,
  submitting,
}: Props) {
  const [title, setTitle] = useState(ipSeed?.ipName ?? '');
  const [genreTags, setGenreTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle(ipSeed?.ipName ?? '');
    const seed = (initialGenreTags ?? [])
      .map((t) => (typeof t === 'string' ? t.trim() : ''))
      .filter(Boolean);
    // 去重 · 保持原顺序
    const uniq = Array.from(new Set(seed));
    setGenreTags(uniq);
    setTagDraft('');
  }, [open, ipSeed, initialGenreTags]);

  const toggleGenre = (g: string) =>
    setGenreTags((cur) => (cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]));

  const pushTag = () => {
    const t = tagDraft.trim();
    if (!t) return;
    if (!genreTags.includes(t)) setGenreTags((cur) => [...cur, t]);
    setTagDraft('');
  };
  const onTagKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      pushTag();
    }
  };

  const submit = () => {
    if (!title.trim()) return;
    if (genreTags.length === 0) return;
    onConfirm({ title: title.trim(), genreTags });
  };

  const audienceTags = [
    audience.primary,
    audience.gender && audience.gender !== 'all'
      ? audience.gender === 'female'
        ? '女性向'
        : '男性向'
      : null,
    audience.ageRange ? `${audience.ageRange[0]}-${audience.ageRange[1]} 岁` : null,
    ...(audience.regions ?? []),
    ...(audience.tags ?? []),
  ].filter(Boolean) as string[];

  const characters = [
    ipSeed?.mcArchetype ? { label: '主角', value: ipSeed.mcArchetype } : null,
    ipSeed?.antagonistArchetype ? { label: '反派', value: ipSeed.antagonistArchetype } : null,
  ].filter(Boolean) as { label: string; value: string }[];

  const hasFull = plotsCount > 0 || Boolean(fullStorylineText);
  const canSubmit = !!title.trim() && genreTags.length > 0 && hasFull && audience.locked && !submitting;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="创建 IP 项目 · 进入编剧 Agent 工作流"
      description="确认 IP 元素后,Agent 会按选定集数严格生成完整无删减分集脚本,并补齐 Story Atlas 各 tab"
      className="max-w-xl"
      footer={
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>
            取消
          </Button>
          <Button
            variant="agent"
            agentColor="writer"
            size="sm"
            onClick={submit}
            disabled={!canSubmit}
            title={
              !audience.locked
                ? '请先锁定受众画像'
                : !hasFull
                ? '请先生成剧情脚本'
                : !title.trim()
                ? '请填写剧名'
                : genreTags.length === 0
                ? '请选择题材标签'
                : ''
            }
          >
            <Rocket className="h-4 w-4" />
            {submitting ? '初始化中…' : '确认 · 启动 Agent'}
          </Button>
        </div>
      }
    >
      <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1 text-sm">
        <Field label="短剧名称">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="如:沈晚的逆袭日记"
            className="h-9"
            disabled={submitting}
          />
        </Field>

        <Field label="题材标签(必选,至少一个)">
          <div className="flex flex-wrap gap-1.5">
            {GENRE_PRESETS.map((g) => {
              const active = genreTags.includes(g);
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => toggleGenre(g)}
                  disabled={submitting}
                  className={
                    active
                      ? 'rounded-full bg-brand-500 px-3 py-1 text-xs text-white shadow-sm'
                      : 'rounded-full border border-outline-variant/40 bg-surface px-3 py-1 text-xs text-neutral-700 transition hover:border-brand-400'
                  }
                >
                  {g}
                </button>
              );
            })}
          </div>
          {genreTags.filter((g) => !GENRE_PRESETS.includes(g)).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {genreTags
                .filter((g) => !GENRE_PRESETS.includes(g))
                .map((g) => (
                  <span
                    key={g}
                    className="inline-flex items-center gap-1 rounded-full bg-warning-soft/60 px-2.5 py-1 text-xs text-warning"
                  >
                    {g}
                    <button
                      type="button"
                      onClick={() => setGenreTags((cur) => cur.filter((x) => x !== g))}
                      className="rounded-full text-warning transition hover:opacity-70"
                      aria-label="移除"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
            </div>
          )}
          <Input
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={onTagKey}
            onBlur={pushTag}
            placeholder="或自定义,回车 / 逗号添加"
            className="mt-1 h-8 text-xs"
            disabled={submitting}
          />
          {genreTags.length > 0 && <HongguoHint genreTags={genreTags} />}
        </Field>

        <Field label="主要人物">
          {characters.length > 0 ? (
            <div className="space-y-1.5">
              {characters.map((c) => (
                <div
                  key={c.label}
                  className="flex items-start gap-2 rounded-lg border border-outline-variant/30 bg-surface-variant/30 px-3 py-2"
                >
                  <span className="rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700">
                    {c.label}
                  </span>
                  <span className="flex-1 text-sm text-neutral-700">{c.value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-outline-variant/40 px-3 py-2 text-xs text-neutral-500">
              — 雷达未生成人物原型,Agent 会按大纲推导补全
            </p>
          )}
        </Field>

        <Field label="剧情大纲">
          <p className="max-h-32 overflow-y-auto whitespace-pre-line rounded-lg bg-surface-variant/40 p-3 text-xs leading-relaxed text-neutral-700">
            {storylineQuick || '— 尚未生成快看'}
          </p>
        </Field>

        <Field label={`剧情脚本${hasFull ? '' : ' · 尚未生成'}`}>
          {hasFull ? (
            <p className="rounded-lg bg-surface-variant/40 p-3 text-xs leading-relaxed text-neutral-700">
              {plotsCount > 0
                ? `已生成 ${plotsCount} 集结构化剧情脚本（世界观 + 人物 + 逐集正文）`
                : fullStorylineText}
            </p>
          ) : (
            <p className="rounded-lg border border-dashed border-warning/40 bg-warning-soft/30 px-3 py-2 text-xs text-warning">
              请先返回上方生成「剧情脚本」后再启动 Agent
            </p>
          )}
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="目标人群">
            {audience.locked ? (
              <div className="flex flex-wrap gap-1">
                {audienceTags.length > 0 ? (
                  audienceTags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full bg-success/10 px-2 py-0.5 text-xs text-success"
                    >
                      {t}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-neutral-500">已锁定但未设标签</span>
                )}
              </div>
            ) : (
              <p className="rounded-lg border border-dashed border-warning/40 bg-warning-soft/30 px-3 py-2 text-xs text-warning">
                请先锁定受众画像
              </p>
            )}
          </Field>
          <Field label="分集数">
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs text-brand-700">
              <Sparkles className="h-3 w-3" /> {episodeCount} 集
            </span>
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs uppercase tracking-wider text-outline">{label}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
